// The opportunity page's reads (apps/server/src/opportunityPage.mjs).
import { authenticatedFetch } from '../auth/authClient';

export interface ScheduleLine {
  line_id: string;
  name: string;
  product_code: string | null;
  billing: string;
  stream: 'Consulting' | 'Studio' | 'Software' | 'Other';
  start: string;
  end: string;
  total: number;
}

export interface OpportunitySummary {
  opportunity: { id: string; name: string; stage: string; probability: number | null; close_date: string | null; next_step: string | null; closed: boolean; won: boolean; lost: boolean };
  quote: { id: string; quote_number: string | null; name: string | null } | null;
  contract_value: number;
  one_time_value: number;
  recurring_value: number;
  weighted_value: number;
  term: { start: string; end: string } | null;
  schedule_lines: ScheduleLine[];
}

export type PlanGroup = 'line' | 'component' | 'stream';

export interface PlanRow {
  key: string;
  kind: 'line' | 'component' | 'stream';
  label: string;
  stream: string;
  product_code?: string | null;
  parent_key?: string;
  share?: number;
  amounts: number[];
  total: number;
}

export interface RevenuePlan {
  months: string[];
  factor: number;
  rows: PlanRow[];
  earned: { amounts: number[]; total: number };
  invoiced: { amounts: number[]; total: number };
  deferred: { amounts: number[] };
  hours: { amounts: number[]; total: number };
}

export interface CellRecord {
  id: string;
  object: 'revenue' | 'billing-schedules';
  line_name: string;
  period_start?: string;
  period_end?: string;
  planned_date?: string;
  label?: string;
  status?: string;
  amount: number;
  hours?: number | null;
}

export interface PlanCell {
  row: string;
  month: string;
  note: string | null;
  object: 'revenue' | 'billing-schedules';
  total: number;
  records: CellRecord[];
}

export interface DeliveryPerson { app_user_id: string | null; name: string; unstaffed?: boolean; hours: number[]; total: number }
export interface DeliveryRole { role_id: string; name: string; hours: number[]; total: number; staffed: number; people: DeliveryPerson[] }
export interface Delivery { months: string[]; roles: DeliveryRole[]; totals: { hours: number[]; total: number } }

export interface ActivityItem {
  id: string;
  type: string;
  at: string;
  title: string;
  detail: string | null;
  actor: string | null;
  link: { object: string; id: string } | null;
}

async function get<T>(path: string): Promise<T> {
  const res = await authenticatedFetch(path);
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok || !body) throw new Error(body?.error ?? `Could not load (${res.status}).`);
  return body;
}

const base = (id: string) => `/api/opportunities/${encodeURIComponent(id)}`;

export const loadSummary = (id: string) => get<OpportunitySummary>(`${base(id)}/summary`);
export const loadPlan = (id: string, group: PlanGroup, weighted: boolean) =>
  get<RevenuePlan>(`${base(id)}/revenue-plan?group=${group}&weighted=${weighted ? 1 : 0}`);
export const loadCell = (id: string, row: string, month: string) =>
  get<PlanCell>(`${base(id)}/revenue-plan/cell?row=${encodeURIComponent(row)}&month=${month}`);
export const loadDelivery = (id: string) => get<Delivery>(`${base(id)}/delivery`);
/** An opportunity's, a contact's, an account's, a lead's or a project's timeline (contactPage.mjs, accountPage.mjs, leadPage.mjs and projectPage.mjs read theirs the same way). */
export const loadActivity = (object: 'opportunities' | 'contacts' | 'accounts' | 'leads' | 'projects', id: string, limit = 50) =>
  get<{ items: ActivityItem[]; total: number }>(`/api/${object}/${encodeURIComponent(id)}/activity?limit=${limit}`);
